/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { concat } from 'lodash';
import React, { useMemo } from 'react';
import { sessionStorageProvider } from '@opengeoweb/shared';
import {
  transformAirmetToWarningFormat,
  transformSigmetToWarningFormat,
} from '../utils/sig-airmet-util';
import { useAirmetList, useSigmetList } from './aviation-api/hooks';
import { usePublicWarnings } from './warning-api/hooks';
import type { TimeFilter } from '../components/FilterList/filter-utils';
import type { Warning } from '../store/warningsForm/types';

interface WarningsResponse {
  combinedWarnings: Warning[];
  refetchWarnings: () => void;
  isFetching: boolean;
  error?: Error;
  dataUpdatedAt: number;
}

export const useWarnings = (
  disabled?: boolean,
  timeFilter?: TimeFilter,
): WarningsResponse => {
  const config = sessionStorageProvider.getConfig();
  const hasPublicWarningsBaseUrl = Boolean(config.GW_DRAWINGS_BASE_URL);
  const hasAirmetBaseUrl = Boolean(config.GW_AIRMET_BASE_URL);
  const hasSigmetBaseUrl = Boolean(config.GW_SIGMET_BASE_URL);
  const isPublicWarningsDisabled = disabled || !hasPublicWarningsBaseUrl;
  const isAirmetDisabled = disabled || !hasAirmetBaseUrl;
  const isSigmetDisabled = disabled || !hasSigmetBaseUrl;

  const {
    data: publicWarnings,
    refetch: refetchPublicWarnings,
    isFetching: isFetchingPublicWarnings,
    error: publicWarningsError,
    dataUpdatedAt,
  } = usePublicWarnings(isPublicWarningsDisabled, timeFilter);
  const {
    data: airmetWarnings,
    refetch: refetchAirmet,
    isFetching: isFetchingAirmet,
  } = useAirmetList(isAirmetDisabled, timeFilter);
  const {
    data: sigmetWarnings,
    refetch: refetchSigmet,
    isFetching: isFetchingSigmet,
  } = useSigmetList(isSigmetDisabled, timeFilter);

  const combinedWarnings = useMemo(() => {
    const transformedSigmets =
      sigmetWarnings?.map((sigmet) => transformSigmetToWarningFormat(sigmet)) ||
      [];
    const transformedAirmets =
      airmetWarnings?.map((airmet) => transformAirmetToWarningFormat(airmet)) ||
      [];

    return concat<Warning>(
      publicWarnings || [],
      transformedSigmets,
      transformedAirmets,
    );
  }, [sigmetWarnings, airmetWarnings, publicWarnings]);

  const refetchWarnings = (): void => {
    if (hasPublicWarningsBaseUrl) {
      void refetchPublicWarnings();
    }
    if (hasAirmetBaseUrl) {
      void refetchAirmet();
    }
    if (hasSigmetBaseUrl) {
      void refetchSigmet();
    }
  };

  const isFetching =
    isFetchingPublicWarnings || isFetchingAirmet || isFetchingSigmet;

  return {
    combinedWarnings,
    refetchWarnings,
    isFetching,
    error: publicWarningsError || undefined,
    dataUpdatedAt,
  };
};

export function useAutoRefetchOnExpiry(
  combinedWarnings: Warning[],
  refetchWarnings: () => void,
): void {
  React.useEffect(() => {
    if (!combinedWarnings || combinedWarnings.length === 0) {
      return undefined;
    }

    const now = new Date();
    const futureEnds: Date[] = combinedWarnings
      .map((wn) => new Date(wn.warningDetail.validUntil))
      .filter((date) => date > now);

    if (futureEnds.length === 0) {
      return undefined;
    }

    const nextExpiry = Math.min(...futureEnds.map((date) => date.getTime()));
    const msUntilNext = nextExpiry - now.getTime();

    const timeout = setTimeout(() => {
      refetchWarnings();
    }, msUntilNext + 1000);

    return (): void => {
      clearTimeout(timeout);
    };
  }, [combinedWarnings, refetchWarnings]);
}
